import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { Trash2, Plus, Search } from "lucide-react";

type SuppressionEntry = {
  _id: string;
  email: string;
  reason: string;
  suppressedAt: string;
  notes?: string;
};

const REASON_COLORS: Record<string, string> = {
  hard_bounce:  "bg-red-100 text-red-800",
  complaint:    "bg-orange-100 text-orange-800",
  unsubscribed: "bg-gray-100 text-gray-700",
  soft_bounce:  "bg-yellow-100 text-yellow-800",
};

const PROTECTED = new Set(["hard_bounce", "complaint"]);

export default function SuppressionTab() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [reasonFilter, setReasonFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newReason, setNewReason] = useState("unsubscribed");
  const [newNotes, setNewNotes] = useState("");
  const [removing, setRemoving] = useState<string | null>(null);
  const qc = useQueryClient();

  // Debounce search input — wait 300ms after typing stops before firing a query
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const qs = new URLSearchParams({ page: String(page), limit: "25" });
  if (search) qs.set("search", search);
  if (reasonFilter !== "all") qs.set("reason", reasonFilter);

  const { data, isLoading } = useQuery<any>({
    queryKey: ["/api/admin/email/suppression", page, search, reasonFilter],
    queryFn: () => apiRequest("GET", `/api/admin/email/suppression?${qs}`) as unknown as Promise<any>,
    keepPreviousData: true,
  } as any);

  const { data: summary } = useQuery({
    queryKey: ["/api/admin/email/suppression/summary"],
    queryFn: () => apiRequest("GET", "/api/admin/email/suppression/summary") as unknown as Promise<any>,
    staleTime: 60_000,
  });

  async function addSuppression() {
    if (!newEmail.trim()) return showError("Email is required");
    try {
      await apiRequest("POST", "/api/admin/email/suppression", { email: newEmail.trim(), reason: newReason, notes: newNotes.trim() || undefined });
      showSuccess(`${newEmail} suppressed (${newReason})`);
      setNewEmail(""); setNewNotes(""); setAdding(false);
      qc.invalidateQueries({ queryKey: ["/api/admin/email/suppression"] });
    } catch (e: any) {
      showError(e.message || "Failed to add suppression");
    }
  }

  async function removeSuppression(email: string, reason: string) {
    const force = PROTECTED.has(reason) ? "?force=true" : "";
    if (PROTECTED.has(reason) && !window.confirm(`${reason} suppressions are protected. Remove anyway?`)) return;
    setRemoving(email);
    try {
      await apiRequest("DELETE", `/api/admin/email/suppression/${encodeURIComponent(email)}${force}`);
      showSuccess(`Suppression removed for ${email}`);
      qc.invalidateQueries({ queryKey: ["/api/admin/email/suppression"] });
    } catch (e: any) {
      showError(e.message || "Failed to remove suppression");
    } finally {
      setRemoving(null);
    }
  }

  const entries: SuppressionEntry[] = data?.data || [];
  const total: number = data?.total || 0;
  const totalPages = Math.ceil(total / 25);

  return (
    <div className="space-y-4">
      {/* Summary badges */}
      {summary && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(summary).filter(([k]) => k !== "total").map(([reason, count]) => (
            <span key={reason} className={`text-xs px-2.5 py-1 rounded-full font-medium ${REASON_COLORS[reason] || "bg-gray-100 text-gray-700"}`}>
              {reason.replace("_", " ")}: {count as number}
            </span>
          ))}
          {summary.total != null && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 font-medium">
              total: {summary.total as number}
            </span>
          )}
        </div>
      )}

      {/* Filters + Add */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search email…"
            value={searchInput}
            onChange={e => { setSearchInput(e.target.value); setPage(1); }}
            className="pl-8 h-8 text-sm"
          />
        </div>
        <Select value={reasonFilter} onValueChange={v => { setReasonFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40 h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All reasons</SelectItem>
            <SelectItem value="hard_bounce">Hard bounce</SelectItem>
            <SelectItem value="soft_bounce">Soft bounce</SelectItem>
            <SelectItem value="complaint">Complaint</SelectItem>
            <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => setAdding(a => !a)}>
          <Plus className="h-3.5 w-3.5" /> Add
        </Button>
      </div>

      {/* Add form */}
      {adding && (
        <Card className="border-dashed">
          <CardContent className="pt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="user@example.com" className="h-8 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Reason</Label>
                <Select value={newReason} onValueChange={setNewReason}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                    <SelectItem value="hard_bounce">Hard bounce</SelectItem>
                    <SelectItem value="soft_bounce">Soft bounce</SelectItem>
                    <SelectItem value="complaint">Complaint</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notes (optional)</Label>
              <Input value={newNotes} onChange={e => setNewNotes(e.target.value)} placeholder="Why suppressed?" className="h-8 text-sm" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={addSuppression}>Add suppression</Button>
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{total} suppressed address{total !== 1 ? "es" : ""}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && <div className="p-4 text-sm text-muted-foreground">Loading…</div>}
          {!isLoading && entries.length === 0 && <div className="p-4 text-sm text-muted-foreground">No suppressions found.</div>}
          <div className="divide-y">
            {entries.map(e => (
              <div key={e._id} className="flex items-center justify-between px-4 py-2.5 gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-mono truncate">{e.email}</div>
                  {e.notes && <div className="text-xs text-muted-foreground truncate">{e.notes}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${REASON_COLORS[e.reason] || "bg-gray-100 text-gray-700"}`}>
                    {e.reason.replace("_", " ")}
                  </span>
                  <span className="text-xs text-muted-foreground">{new Date(e.suppressedAt).toLocaleDateString()}</span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                    disabled={removing === e.email}
                    onClick={() => removeSuppression(e.email, e.reason)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</Button>
          <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
          <Button size="sm" variant="outline" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>Next →</Button>
        </div>
      )}
    </div>
  );
}
